import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppData } from "../state/AppData";
import { collectTags, toggleTagKey } from "../lib/nodeTags";

// Several tags at once; a node matches when it has any of the chosen tags. Shared by the
// Nodes tab and the map. Absent when no node carries a tag.
export function TagFilter() {
  const { nodes, tagFilter, setTagFilter } = useAppData();
  const [open, setOpen] = useState(false);
  const tags = useMemo(() => collectTags(nodes.values()), [nodes]);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [align, setAlign] = useState<"left" | "right">("left");
  const [top, setTop] = useState<number | null>(null);

  // Keep the menu fully inside the viewport: left-aligned to the button, or right-aligned when
  // that would run past the right edge (re-measured whenever it opens or the tag list changes).
  useLayoutEffect(() => {
    if (!open || !rootRef.current || !menuRef.current) return;
    const btn = rootRef.current.getBoundingClientRect();
    const width = menuRef.current.offsetWidth;
    const vw = document.documentElement.clientWidth;
    setAlign(btn.left + width > vw - 8 ? "right" : "left");
    // On a phone the menu is fixed to the screen: start it just under the button, never over the top nav.
    setTop(vw <= 600 ? Math.round(btn.bottom + 6) : null);
  }, [open, tags]);

  // Close on a click outside the control or on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (tags.length === 0) return null;

  return (
    <div className="tag-filter" ref={rootRef} style={{ position: "relative" }}>
      <button type="button" className="region-filter-btn" data-active={tagFilter.length > 0} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {tagFilter.length === 0 ? "Any Tag" : `Tags (${tagFilter.length})`}
      </button>
      {open && (
        <div className="tag-filter-menu" ref={menuRef} data-align={align} role="group" aria-label="Tags" style={top === null ? undefined : { top, maxHeight: `calc(100vh - ${top + 16}px)` }}>
          <div className="tag-filter-head">
            <span className="tag-filter-mode" title="A node matches when it has any chosen tag">Any Of</span>
            <button type="button" className="tag-filter-close" aria-label="Close" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          {tags.map((t) => (
            <label key={t.key} className="tag-filter-row">
              <input type="checkbox" checked={tagFilter.includes(t.key)} onChange={() => setTagFilter(toggleTagKey(tagFilter, t.key))} />
              <span>{t.tag}</span>
              <span className="tag-filter-count">{t.count}</span>
            </label>
          ))}
          {tagFilter.length > 0 && (
            <button type="button" className="tag-filter-clear" onClick={() => setTagFilter([])}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
